import React, { useCallback, useEffect, useState } from 'react'
import { FormSkeleton } from './PageSkeletons'
import { createPortal } from 'react-dom'
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  History,
  Loader2,
  RefreshCw,
  RotateCcw,
  Send,
  X,
  XCircle,
} from 'lucide-react'
import { nsApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { formatWhen, formatAge, formatWaiting } from '../utils/bcTime'
import PDFViewer from './PDFViewer'

// ─── TemplateNSDashboard ──────────────────────────────────────────────────────
//
// Per-template NetSuite operations view — deliberately the same shape as
// TemplateBCDashboard.jsx (stat tiles, retry queue, pending approvals,
// recently posted, a document preview and a posting-history modal), so an
// admin who already knows one connector's dashboard already knows this one.
//
// Two real differences from BC, both structural, not cosmetic:
//   - NetSuite has no approval-cycle table (bc_post_cycles). ns_post_log is
//     the single ledger, so there is no per-cycle grouping to show in the
//     history modal — just the flat attempt list.
//   - Retry dispatches through nsApi.retryNsRecord(nsEntityType, ...), the
//     one endpoint that already knows how to route 'purchase_order',
//     'vendor_bid', or any admin-configured generic record type to its own
//     retry function (netsuite_service.retry_ns_record) — never a
//     PO-specific or Bid-specific call, so this same dashboard works
//     unmodified for a generic template, exactly as the fields tab does.

const PERIODS = [
  { key: 'today',    label: 'Today' },
  { key: 'week',     label: '7 days' },
  { key: 'month',    label: '30 days' },
  { key: 'all_time', label: 'All time' },
]

function waitTone(hours) {
  if (hours == null) return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
  if (hours >= 48) return 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
  if (hours >= 24) return 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300'
  return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
}


// ─── Document preview ───────────────────────────────────────────

function DocumentPreviewModal({ documentId, title, onClose }) {
  if (!documentId) return null
  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-4xl h-[85vh] bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-gray-800">
          <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate flex-1">
            {title || 'Source document'}
          </p>
          <button
            onClick={onClose}
            aria-label="Close preview"
            className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0">
          <PDFViewer documentId={documentId} />
        </div>
      </div>
    </div>,
    document.body
  )
}


// ─── Posting history ────────────────────────────────────────────

function HistoryModal({ requestId, title, onClose }) {
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState('')

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    nsApi.getDocumentHistory(requestId)
      .then(res => { if (!cancelled) setData(res.data || null) })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.error || 'Could not load posting history.') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [requestId])

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <History className="h-4 w-4 text-navy-600 dark:text-navy-400 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Posting history</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{title || `Request #${requestId}`}</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close history"
            className="inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          )}
          {!loading && error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {!loading && !error && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                Post attempts
              </p>
              {data?.attempts?.length ? (
                <div className="space-y-1.5">
                  {data.attempts.map((a, i) => (
                    <div key={a.id ?? i} className="flex items-start gap-2 rounded-lg bg-gray-50 dark:bg-gray-800/50 px-3 py-2">
                      {a.status === 'success'
                        ? <CheckCircle className="h-3.5 w-3.5 text-navy-500 mt-0.5 flex-shrink-0" />
                        : <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 flex-shrink-0" />}
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-700 dark:text-gray-300">
                          Attempt {a.attempt_number} · {a.status}
                          <span className="text-gray-400 dark:text-gray-500">
                            {' '}· {formatWhen(a.created_at)}
                          </span>
                        </p>
                        {a.ns_po_number && (
                          <p className="text-[11px] font-mono text-gray-600 dark:text-gray-300 mt-0.5">
                            {a.ns_po_number}
                          </p>
                        )}
                        {a.error_message && (
                          <p className="text-[11px] text-red-600 dark:text-red-400 mt-0.5 break-words">
                            {a.error_message}
                          </p>
                        )}
                        {a.ns_url && (
                          <a
                            href={a.ns_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-medium text-navy-600 dark:text-navy-400 hover:underline mt-0.5"
                          >
                            Open in NetSuite <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">No post attempts recorded.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}


// ─── Shared row chrome ──────────────────────────────────────────

function DocButton({ documentId, onPreview }) {
  if (!documentId) {
    return <span className="inline-flex h-7 w-7 items-center justify-center text-gray-300 dark:text-gray-600" title="No source document"><FileText className="h-4 w-4" /></span>
  }
  return (
    <button
      onClick={() => onPreview(documentId)}
      title="Preview the original document"
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:text-navy-600 hover:bg-navy-50 dark:hover:bg-navy-900/20 dark:hover:text-navy-400 transition-colors"
    >
      <FileText className="h-4 w-4" />
    </button>
  )
}

function SectionCard({ icon: Icon, title, subtitle, count, children }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-navy-50 dark:bg-navy-900/20 flex-shrink-0">
          <Icon className="h-4 w-4 text-navy-600 dark:text-navy-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
        </div>
        {count > 0 && (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 flex-shrink-0">
            {count}
          </span>
        )}
      </div>
      {children}
    </div>
  )
}

function EmptyRow({ children }) {
  return <p className="px-5 py-6 text-center text-xs text-gray-400 dark:text-gray-500">{children}</p>
}


// ─── Dashboard ──────────────────────────────────────────────────

function TemplateNSDashboard({ templateId, templateName, nsEntityType }) {
  const { user } = useAuth()
  const toast = useToast()

  const [data, setData]         = useState(null)
  const [loading, setLoading]   = useState(true)
  const [error, setError]       = useState('')
  const [period, setPeriod]     = useState('week')
  const [busyId, setBusyId]     = useState(null)
  const [preview, setPreview]   = useState(null)   // documentId
  const [history, setHistory]   = useState(null)   // { requestId, title }

  const load = useCallback(async (quiet = false) => {
    if (!templateId) return
    if (!quiet) setLoading(true)
    try {
      const res = await nsApi.getTemplateDashboard(templateId)
      setData(res.data || null)
      setError('')
    } catch (e) {
      setError(e?.response?.data?.error || 'Could not load the NetSuite dashboard.')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [templateId])

  useEffect(() => { load() }, [load])

  const retry = async (row) => {
    setBusyId(row.request_id)
    try {
      // One dispatcher for every document type this template could be —
      // 'purchase_order' and 'vendor_bid' delegate to their own hand-written
      // retry paths server-side, anything else routes through the generic
      // engine. Never a PO-specific or Bid-specific call from here.
      const res = await nsApi.retryNsRecord(data?.ns_entity_type || nsEntityType, {
        request_id: row.request_id,
        org_id: user?.organization_id,
      })
      const r = res.data || {}
      if (r.success) {
        toast.success('Posted to NetSuite', r.ns_po_number || '')
      } else {
        toast.warning('Retry did not post', r.error || 'NetSuite rejected the document again.')
      }
    } catch (e) {
      const body = e?.response?.data || {}
      toast.error('Retry failed', body.error || e.message || 'Could not retry the post.')
    } finally {
      setBusyId(null)
      load(true)
    }
  }

  if (loading) {
    return (
      <FormSkeleton />
    )
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">Dashboard unavailable</p>
            <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">{error}</p>
            <button
              onClick={() => load()}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-medium transition-colors"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  const stats   = data?.stats || {}
  const posted  = stats.posted?.[period] ?? 0
  const failed  = stats.failed?.[period] ?? 0
  const pending = stats.pending_now ?? 0
  const periodLabel = PERIODS.find(p => p.key === period)?.label.toLowerCase()

  return (
    <div className="space-y-4 max-w-3xl">

      {/* Period selector + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="inline-flex items-center h-9 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-100/70 dark:bg-gray-800/70 p-0.5">
          {PERIODS.map(p => (
            <button
              key={p.key}
              onClick={() => setPeriod(p.key)}
              className={`px-3 h-8 rounded-md text-xs font-medium transition-all ${
                period === p.key
                  ? 'bg-white dark:bg-gray-900 text-navy-700 dark:text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => load()}
          className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Stat tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-navy-500" />
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Posted</p>
          </div>
          <p className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{posted}</p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{periodLabel}</p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center gap-2">
            <XCircle className={`h-4 w-4 ${failed > 0 ? 'text-red-500' : 'text-gray-300 dark:text-gray-600'}`} />
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Failed</p>
          </div>
          <p className={`mt-2 text-2xl font-bold ${failed > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>{failed}</p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{periodLabel} · distinct documents</p>
        </div>
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center gap-2">
            <Clock className={`h-4 w-4 ${pending > 0 ? 'text-amber-500' : 'text-gray-300 dark:text-gray-600'}`} />
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Pending approval</p>
          </div>
          <p className={`mt-2 text-2xl font-bold ${pending > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'}`}>{pending}</p>
          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">waiting right now</p>
        </div>
      </div>

      {/* Failed posts — retry queue */}
      <SectionCard
        icon={AlertTriangle}
        title="Failed posts"
        subtitle="Documents NetSuite rejected. Retry after fixing the cause."
        count={data?.failed?.length || 0}
      >
        {!data?.failed?.length ? (
          <EmptyRow>Nothing has failed to post.</EmptyRow>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {data.failed.map(row => (
              <div key={row.request_id} className="px-5 py-3">
                <div className="flex items-start gap-3">
                  <DocButton documentId={row.document_id} onPreview={setPreview} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {row.title || `Request #${row.request_id}`}
                    </p>
                    {row.error && (
                      <p className="text-[11px] text-red-600 dark:text-red-400 mt-0.5 line-clamp-2 break-words">
                        {row.error}
                      </p>
                    )}
                    <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                      {row.attempts} attempt{row.attempts === 1 ? '' : 's'}
                      {row.last_attempt_at && ` · last ${formatAge(row.last_attempt_at)}`}
                      {row.attempts >= 3 && (
                        <span className="text-amber-600 dark:text-amber-400"> · retry limit reached</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <button
                      onClick={() => setHistory({ requestId: row.request_id, title: row.title })}
                      title="Posting history"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      <History className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => retry(row)}
                      disabled={busyId === row.request_id || row.attempts >= 3}
                      title={row.attempts >= 3 ? 'Maximum of 3 attempts reached' : 'Retry posting to NetSuite'}
                      className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-xs font-semibold text-white bg-[var(--token-primary,#1946FA)] hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                    >
                      {busyId === row.request_id
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RotateCcw className="h-3.5 w-3.5" />}
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Pending approval */}
      <SectionCard
        icon={Clock}
        title="Waiting for approval"
        subtitle="Oldest first — these cannot post until an approver acts."
        count={data?.pending_approval?.length || 0}
      >
        {!data?.pending_approval?.length ? (
          <EmptyRow>No documents are waiting on an approver.</EmptyRow>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {data.pending_approval.map(row => (
              <div key={row.request_id} className="px-5 py-3 flex items-center gap-3">
                <DocButton documentId={row.document_id} onPreview={setPreview} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {row.title || `Request #${row.request_id}`}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    {row.assigned_to ? `Assigned to ${row.assigned_to}` : 'Unassigned'}
                    {row.waiting_since && ` · since ${formatWhen(row.waiting_since)}`}
                  </p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${waitTone(row.waiting_hours)}`}>
                  {formatWaiting(row.waiting_hours)}
                </span>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {/* Recently posted */}
      <SectionCard
        icon={Send}
        title="Recently posted"
        subtitle="Successful posts, with a direct link into the NetSuite web client."
        count={data?.recent?.length || 0}
      >
        {!data?.recent?.length ? (
          <EmptyRow>Nothing has been posted from this template yet.</EmptyRow>
        ) : (
          <div className="divide-y divide-gray-50 dark:divide-gray-800">
            {data.recent.map(row => (
              <div key={row.request_id} className="px-5 py-3 flex items-center gap-3">
                <DocButton documentId={row.document_id} onPreview={setPreview} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {row.title || `Request #${row.request_id}`}
                  </p>
                  <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">
                    <span className="font-mono text-gray-600 dark:text-gray-300">
                      {row.ns_po_number || row.ns_internal_id || '—'}
                    </span>
                    {row.posted_at && ` · ${formatAge(row.posted_at)}`}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => setHistory({ requestId: row.request_id, title: row.title })}
                    title="Posting history"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <History className="h-4 w-4" />
                  </button>
                  {row.ns_url ? (
                    <a
                      href={row.ns_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open this record in NetSuite"
                      className="inline-flex items-center gap-1 px-2.5 h-7 rounded-md text-xs font-semibold text-navy-700 dark:text-navy-300 border border-navy-200 dark:border-navy-800 hover:bg-navy-50 dark:hover:bg-navy-900/20 transition-colors"
                    >
                      Open in NetSuite <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span
                      className="text-[11px] text-gray-400 dark:text-gray-500 px-2"
                      title="This record type has no confirmed web-client link, so no deep link can be built."
                    >
                      no link
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      {preview && (
        <DocumentPreviewModal
          documentId={preview}
          title={templateName}
          onClose={() => setPreview(null)}
        />
      )}
      {history && (
        <HistoryModal
          requestId={history.requestId}
          title={history.title}
          onClose={() => setHistory(null)}
        />
      )}
    </div>
  )
}

export default TemplateNSDashboard
